import { Injectable, computed, inject } from '@angular/core';

import { GraphQLClientService } from './graphql-client.service';
import { isErr, makeError } from '../models/api-result';
import { createApiState } from '../models/api-state';
import { cleanAsset, type LogoSet } from '../utils/brand-logo';

/**
 * The organisation's logos for the workspace mark — the main logo and its
 * light- and dark-paper versions.
 *
 * WHY A QUERY OF ITS OWN. The mark is the customer's (One Paper: "the
 * organisation's mark, a fitted logo or initials"), and `viewer.customer`
 * carries only `logo`. The versions live on the same customer document but
 * are only served by `myCompany`. Without them the mark printed one asset on
 * every paper, so a dark-ink logo vanished on Graphite. Manage and Insights
 * (#66) already pick the version for the paper; this lets Settings do the same.
 *
 * Optional and best-effort: a failure leaves the viewer's `customer.logo` in
 * place, which is exactly what the mark showed before. `@Auth()` only, so any
 * signed-in person may ask. Loaded once the viewer is known, so a chromeless
 * route never sends it.
 */

const ORGANISATION_MARK_QUERY = `
  query OrganisationMark {
    myCompany { logo logoDark logoLight }
  }
`;

@Injectable({ providedIn: 'root' })
export class OrganisationMarkService {
  private readonly graphql = inject(GraphQLClientService);

  /**
   * `data` is the organisation's logos, blanks dropped — null until (and
   * unless) answered. `error` is the last failure, for diagnosis only: the
   * mark falls back to the viewer's logo, which is a complete answer, not an
   * error state, so nothing renders it.
   */
  private readonly state = createApiState<LogoSet>();
  readonly logos = this.state.data;
  readonly error = this.state.error;

  /** True once any logo version came back. */
  readonly loaded = computed(() => this.state.data() !== null);

  private inflight: Promise<void> | null = null;

  /** Read the logos once (single-flight). Never throws. */
  load(): Promise<void> {
    this.inflight ??= this.fetch();
    return this.inflight;
  }

  private async fetch(): Promise<void> {
    this.state.setLoading();
    // Background: nobody clicked for this, so a 401 here must not sign anyone out.
    const result = await this.graphql.queryResult<LogoSet | null>(
      ORGANISATION_MARK_QUERY,
      undefined,
      'cache-first',
      { background: true },
    );
    if (isErr(result) || !result.data) {
      // Leave the viewer's logo standing; let a later call try again.
      this.state.setError(
        isErr(result)
          ? result.error
          : makeError('EMPTY_RESPONSE', 'Organisation logos were not returned.', { retryable: true }),
      );
      this.inflight = null;
      return;
    }
    const d = result.data;
    this.state.setData({
      logo: cleanAsset(d.logo),
      logoDark: cleanAsset(d.logoDark),
      logoLight: cleanAsset(d.logoLight),
    });
  }
}
